import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  TextField,
  Button,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

// Icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import FavoriteIcon from "@mui/icons-material/Favorite";

const Footer = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    console.log("Subscribe email:", email);
    setSubscribed(true);
    setEmail("");
    setTimeout(() => setSubscribed(false), 3000);
  };

  const footerLinks = {
    company: [
      { label: "About Us", path: "/about" },
      { label: "Contact Us", path: "/contact" },
      { label: "Privacy Policy", path: "/privacy" },
      { label: "Terms of Service", path: "/terms" },
      { label: "HIPAA Compliance", path: "/hipaa" },
      { label: "Careers", path: "/careers" },
    ],
    resources: [
      { label: "Research Library", path: "/resources/library" },
      { label: "Treatment Guidelines", path: "/resources/guidelines" },
      { label: "Find a Specialist", path: "/resources/find-doctor" },
      { label: "Support Organizations", path: "/resources/organizations" },
      { label: "Clinical Calculators", path: "/resources/calculators" },
      { label: "Community Forums", path: "/community/forums" },
    ],
    product: [
      { label: "Clinical Trials Search", path: "/amyloid/clinicaltrialsearch" },
      { label: "Browse All Trials", path: "/trials/browse" },
      { label: "Patient Matching", path: "/trials/patientmatching" },
      {
        label: "AI Trial Matching",
        path: "/trials/smart-match",
        badge: "Plus",
      },
      { label: "My Dashboard", path: "/dashboard" },
      { label: "Trial Alerts", path: "/trials/alerts", badge: "Plus" },
    ],
    tools: [
      {
        label: "Trial Comparison",
        path: "/tools/compare-trials",
        badge: "Plus",
      },
      {
        label: "Eligibility Checker",
        path: "/tools/eligibility",
        badge: "Plus",
      },
      { label: "Travel Planner", path: "/tools/travel", badge: "Plus" },
      { label: "Document Manager", path: "/tools/documents", badge: "Plus" },
      {
        label: "Medication Tracker",
        path: "/tools/medications",
        badge: "Plus",
      },
      { label: "Clinical Trial Glossary", path: "/tools/glossary" },
    ],
    education: [
      { label: "Amyloidosis Basics", path: "/amyloid" },
      { label: "Cardiac Involvement", path: "/amyloid/cardiac" },
      { label: "Neurological Impact", path: "/amyloid/neuro" },
      { label: "Renal Complications", path: "/amyloid/renal" },
      { label: "GI Manifestations", path: "/amyloid/gi" },
      { label: "MSK Involvement", path: "/amyloid/msk" },
    ],
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: "#1a1a2e",
        color: "white",
        pt: 6,
        pb: 3,
        mt: 8,
      }}
    >
      <Container maxWidth="xl">
        {/* Top Section: Brand & Social Media */}
        <Box sx={{ mb: 5, textAlign: "center" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              mb: 2,
            }}
          >
            <Box
              component="img"
              src="/logo.png"
              alt="NephroTrax"
              sx={{ height: 50, width: 50 }}
            />
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              NephroTrax
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.7)",
              maxWidth: 700,
              mx: "auto",
              mb: 3,
              lineHeight: 1.7,
            }}
          >
            Connecting patients, providers, and researchers to accelerate
            amyloidosis treatment and improve lives through innovative
            technology and comprehensive resources.
          </Typography>

          {/* Social Media Icons */}
          <Stack direction="row" spacing={1.5} justifyContent="center">
            <IconButton
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "#1A5EA8",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s",
              }}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "#1A5EA8",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s",
              }}
            >
              <TwitterIcon />
            </IconButton>
            <IconButton
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "#1A5EA8",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s",
              }}
            >
              <LinkedInIcon />
            </IconButton>
            <IconButton
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "#1A5EA8",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s",
              }}
            >
              <InstagramIcon />
            </IconButton>
            <IconButton
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "white",
                bgcolor: "rgba(255,255,255,0.1)",
                "&:hover": {
                  bgcolor: "#1A5EA8",
                  transform: "translateY(-3px)",
                },
                transition: "all 0.3s",
              }}
            >
              <YouTubeIcon />
            </IconButton>
          </Stack>
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 5 }} />

        {/* Links Section: 6 Columns in Same Row */}
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {/* Column 1: Company */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Company
            </Typography>
            <Stack spacing={1.5}>
              {footerLinks.company.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    display: "block",
                    "&:hover": {
                      color: "#1A5EA8",
                      transform: "translateX(4px)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Column 2: Resources */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Resources
            </Typography>
            <Stack spacing={1.5}>
              {footerLinks.resources.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    display: "block",
                    "&:hover": {
                      color: "#1A5EA8",
                      transform: "translateX(4px)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Column 3: Product */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Product
            </Typography>
            <Stack spacing={1.5}>
              {footerLinks.product.map((link) => (
                <Box
                  key={link.path}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Link
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      "&:hover": {
                        color: "#1A5EA8",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    {link.label}
                  </Link>
                  {link.badge && (
                    <Chip
                      label={link.badge}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.65rem",
                        bgcolor: "#ff9800",
                        color: "white",
                      }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Column 4: Tools - NEW! */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Tools
            </Typography>
            <Stack spacing={1.5}>
              {footerLinks.tools.map((link) => (
                <Box
                  key={link.path}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Link
                    component={RouterLink}
                    to={link.path}
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      "&:hover": {
                        color: "#1A5EA8",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s",
                    }}
                  >
                    {link.label}
                  </Link>
                  {link.badge && (
                    <Chip
                      label={link.badge}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.65rem",
                        bgcolor: "#ff9800",
                        color: "white",
                      }}
                    />
                  )}
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Column 5: Education */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Education
            </Typography>
            <Stack spacing={1.5}>
              {footerLinks.education.map((link) => (
                <Link
                  key={link.path}
                  component={RouterLink}
                  to={link.path}
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    textDecoration: "none",
                    fontSize: "0.9rem",
                    display: "block",
                    "&:hover": {
                      color: "#1A5EA8",
                      transform: "translateX(4px)",
                    },
                    transition: "all 0.2s",
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Column 6: Stay Updated */}
          <Grid item xs={12} sm={6} md={2}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, mb: 2.5, fontSize: "1.1rem" }}
            >
              Stay Updated
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.7)", mb: 2 }}
            >
              Get the latest news and clinical trial updates
            </Typography>
            <Box component="form" onSubmit={handleSubscribe}>
              <TextField
                fullWidth
                size="small"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                required
                sx={{
                  mb: 1,
                  "& .MuiOutlinedInput-root": {
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#1A5EA8",
                    },
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "rgba(255,255,255,0.5)",
                  },
                }}
              />
              <Button
                fullWidth
                type="submit"
                variant="contained"
                endIcon={<SendIcon />}
                disabled={subscribed}
                sx={{
                  background: subscribed
                    ? "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                    : "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5568d3 0%, #653a8b 100%)",
                  },
                  "&.Mui-disabled": {
                    background:
                      "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    color: "white",
                  },
                }}
              >
                {subscribed ? "✓ Subscribed!" : "Subscribe"}
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 4 }} />

        {/* Contact Information Bar */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-start" },
                gap: 1,
              }}
            >
              <EmailIcon sx={{ color: "#1A5EA8", fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                reviewer@verainsight.com (or sandeep.sinha@verainsight.com)
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <PhoneIcon sx={{ color: "#1A5EA8", fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                612-859-8928
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "center", md: "flex-end" },
                gap: 1,
              }}
            >
              <LocationOnIcon sx={{ color: "#1A5EA8", fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                Minneapolis, MN
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.1)", mb: 3 }} />

        {/* Bottom Bar: Copyright & Legal */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255,255,255,0.5)",
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            © {new Date().getFullYear()} NephroTrax. All rights reserved.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <Link
              component={RouterLink}
              to="/privacy"
              sx={{
                color: "rgba(255,255,255,0.5)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": { color: "#1A5EA8" },
              }}
            >
              Privacy Policy
            </Link>
            <Typography sx={{ color: "rgba(255,255,255,0.3)" }}>•</Typography>
            <Link
              component={RouterLink}
              to="/terms"
              sx={{
                color: "rgba(255,255,255,0.5)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": { color: "#1A5EA8" },
              }}
            >
              Terms of Service
            </Link>
            <Typography sx={{ color: "rgba(255,255,255,0.3)" }}>•</Typography>
            <Link
              component={RouterLink}
              to="/hipaa"
              sx={{
                color: "rgba(255,255,255,0.5)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": { color: "#1A5EA8" },
              }}
            >
              HIPAA Compliance
            </Link>
            <Typography sx={{ color: "rgba(255,255,255,0.3)" }}>•</Typography>
            <Link
              component={RouterLink}
              to="/accessibility"
              sx={{
                color: "rgba(255,255,255,0.5)",
                textDecoration: "none",
                fontSize: "0.85rem",
                "&:hover": { color: "#1A5EA8" },
              }}
            >
              Accessibility
            </Link>
          </Box>
        </Box>

        {/* Made with Love */}
        <Box sx={{ textAlign: "center", mt: 3 }}>
          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            Made with <FavoriteIcon sx={{ fontSize: 14, color: "#f5576c" }} />{" "}
            for the amyloidosis community
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;